/* eslint-disable no-nested-ternary */
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postAPICall } from "lib/callApi";
import { homeAction } from "redux/slice/home";
import { loginUserAction } from "redux/slice/users";
import Loader from "utils/Loader";
import { getFilterDates, calculateKpiPoints } from "utils/HelperFunctions";
import { fetchCompetitions, updateKpiEntry } from "apis/home";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import { UPDATE_USER_SESSION, USER_CONFIGS } from "lib/constants";
import CircularProgressChart from "./CircularProgressChart";
import UserKpi from "./UserKpi";
import UserCompetition from "./UserCompetition";
import WeeklyTotals from "./WeeklyTotals";
import KpiTemplateModal from "./KpiTemplateModal";

function UserView({ selectedTab, selectedUser }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.loginUser.loginUserState);
  const { data: dailyInfo, loading: dailyInfoLoading } = useSelector(
    (state) => state.home.dailyPointsInformation
  );
  const refetchCompetitions = useSelector((state) => state.home.refetchCompetitions);
  const {
    data: KpiEntries,
    loading: KpiEntriesLoading,
    lastUpdated,
    targetPoints,
    initialLoading: initialKpiEntryLoading,
  } = useSelector((state) => state.home.kpiEntries);
  const {
    activeStreak,
    pointGoals,
    refresh: refreshAchievements,
  } = useSelector((state) => state.home.achievements);

  const [competitions, setCompetitions] = useState([]);
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [isKpiTemplateModalOpen, setIsKpiTemplateModalOpen] = useState(false);

  const kpiLocksDict =
    KpiEntries.reduce((dict, KpiEntry) => {
      dict[KpiEntry.kpiActionId] = KpiEntry.isKpiLocked;
      return dict;
    }, {}) || {};

  const showKpiTemplateModalConfig = "SHOW_KPI_TEMPLATE_MODAL";

  const showNotification = (type, message) => {
    enqueueSnackbar(
      JSON.stringify({
        icon: type,
        title: type === "success" ? "Success" : "Error",
        text: message,
      }),
      {
        variant: "custom",
      }
    );
  };

  const handleKpiTemplateModalClose = async (dontShowAgain) => {
    if (dontShowAgain) {
      const response = await postAPICall(`${USER_CONFIGS}/${user.userCompanyLinkId}`, true, {
        value: false,
        name: showKpiTemplateModalConfig,
      });
      if (!response?.data?.success) {
        // eslint-disable-next-line no-console
        console.log("There was a problem disabling the KPI Template modal");
      }
    }
    setIsKpiTemplateModalOpen(false);
  };

  const getCompetitions = async () => {
    try {
      setCompetitionLoading(true);
      const response = await fetchCompetitions();
      if (response?.data?.success) {
        const competitionsData = [...response.data.data];
        competitionsData.forEach((competition) => {
          competition.actions.forEach((action) => {
            action.prevCount = action.CompetitionAction.count;
          });
        });
        setCompetitions(competitionsData);
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setCompetitionLoading(false);
    } catch (error) {
      setCompetitionLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    // Show KPI Template modal
    if (user.showKPITemplateModal) {
      // Update redux store and user session
      dispatch(loginUserAction.updateShowKPITemplateModal(false));
      postAPICall(UPDATE_USER_SESSION, true, { key: "showKPITemplateModal", value: false });
      setIsKpiTemplateModalOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (user.type === "user") {
      const dates = getFilterDates("Today", user.timezone);

      dispatch(homeAction.getDailyPointsInfoSaga());
      dispatch(homeAction.getKpiEntrySaga(dates));
      getCompetitions(dates);
    }
  }, []);

  useEffect(() => {
    if (!refetchCompetitions) return;
    getCompetitions();
  }, [refetchCompetitions]);

  const refetchKpiEntries = () => {
    const dates = getFilterDates("Today", user.timezone);
    dispatch(homeAction.getKpiEntrySaga(dates));
  };

  const handleCompetitionRemove = (competitionToRemove) => {
    setCompetitions((prevState) => {
      let prevCompetitions = [...prevState];
      prevCompetitions = prevCompetitions.filter(
        (competition) => competition.id !== competitionToRemove
      );
      return prevCompetitions;
    });
  };

  const calculateNewCount = (oldCount, changeCount, currentActionCount, changeInCount) => {
    let count;
    if (oldCount === currentActionCount && typeof changeInCount !== "undefined") {
      if (currentActionCount === 0 && changeInCount > 0) {
        count = changeInCount;
      } else {
        count = currentActionCount + changeInCount;
      }
    } else {
      count = currentActionCount + (changeCount - oldCount);
    }
    if (count > 999) {
      count = 999;
    } else if (count < 1) {
      count = 0;
    }

    return count;
  };

  const generateCount = (oldCount, updatedCount, currentActionCount, action, changeInCount) =>
    action === "increment"
      ? currentActionCount + 1 <= 999
        ? currentActionCount + 1
        : 999
      : action === "decrement"
      ? currentActionCount - 1 > 0
        ? currentActionCount - 1
        : 0
      : calculateNewCount(oldCount, updatedCount, currentActionCount, changeInCount);

  const handleCompetitionUpdateFail = (payload) => {
    const actionId = payload?.[0].kpiActionId;
    setCompetitions((prevState) => {
      const prevCompetitions = [...prevState];
      prevCompetitions.forEach((competition) => {
        if (payload.find((item) => item.competitionId === competition.id)) {
          const actionIndex = competition.actions.findIndex(
            (action) => action.kpiActionId === actionId
          );
          if (actionIndex !== -1) {
            competition.actions[actionIndex].disabled = false;
            competition.actions[actionIndex].CompetitionAction.count =
              competition.actions[actionIndex].prevCount;
          }
        }
      });
      return prevCompetitions;
    });
  };

  const handleCompetitionActivityChange = (updatedActionData, isNotDaily) => {
    const payload = [];

    competitions.forEach((competition, index) => {
      const foundActionIndex = competition.actions.findIndex(
        (action) => action.kpiActionId === updatedActionData.kpiActionId
      );
      const foundAction = competition.actions.find(
        (action) => action.kpiActionId === updatedActionData.kpiActionId
      );

      if (foundActionIndex !== -1) {
        const newCount =
          isNotDaily && competition.id === updatedActionData.competitionId
            ? updatedActionData.count
            : generateCount(
                foundAction.prevCount || foundAction.CompetitionAction.count,
                updatedActionData.count,
                foundAction.CompetitionAction.count,
                updatedActionData.action,
                updatedActionData.changeInCount
              );

        if (
          newCount === foundAction.CompetitionAction.count &&
          !isNotDaily &&
          competition.id === updatedActionData.competitionId
        ) {
          return;
        }

        const competitionActionData = {
          ...foundAction,
          disabled: true,
          CompetitionAction: {
            count: newCount,
            points: foundAction.CompetitionAction.points,
          },
        };

        payload.push({
          competitionId: competition.id,
          count: newCount,
          kpiActionId: updatedActionData.kpiActionId,
          points:
            competition.winCondition === "Total Points"
              ? calculateKpiPoints(foundAction.count, foundAction.points, newCount)
              : 0,
        });

        setCompetitions((prevState) => {
          const prevCompetitions = [...prevState];
          prevCompetitions[index].actions[foundActionIndex] = competitionActionData;
          return prevCompetitions;
        });
      }
    });
    return payload;
  };

  const handleCompetitionActivityUpdateOnSuccess = (data) => {
    setCompetitions((prevCompetitions) => {
      let oldCompetitions = [...prevCompetitions];
      oldCompetitions = oldCompetitions.filter((competition) => {
        const foundCompetition = data.find(
          (responseCompetition) => responseCompetition.competitionId === competition.id
        );
        if (foundCompetition && foundCompetition.competitionFinished) {
          enqueueSnackbar(`Competition: ${competition.name} has ended`, {
            variant: "info",
            autoHideDuration: 5000,
          });
          return false;
        }
        if (foundCompetition && foundCompetition.competitionAlreadyFinished) {
          enqueueSnackbar(`Competition: ${competition.name} has already ended`, {
            variant: "info",
            autoHideDuration: 5000,
          });
          return false;
        }

        return true;
      });
      oldCompetitions.forEach((competition) => {
        const foundCompetition = data.find(
          (responseCompetition) => responseCompetition.competitionId === competition.id
        );
        if (foundCompetition) {
          const foundKPIActionIndex = competition.actions.findIndex(
            (activity) => activity.kpiActionId === foundCompetition.kpi.kpiActionId
          );
          if (foundKPIActionIndex !== -1) {
            competition.rank = foundCompetition.rank;
            competition.actions[foundKPIActionIndex].disabled = false;
            if (typeof competition.actions[foundKPIActionIndex].prevCount !== "undefined") {
              delete competition.actions[foundKPIActionIndex].prevCount;
            }
            competition.actions[foundKPIActionIndex].CompetitionAction = {
              count: foundCompetition.kpi.count,
              points: foundCompetition.kpi.points,
            };
          }
        }
      });
      return oldCompetitions;
    });
  };

  const updateActivity = async (payload) => {
    try {
      const response = await updateKpiEntry(payload);

      if (response?.data?.success) {
        if (payload?.daily) {
          dispatch(
            homeAction.setLastUpdatedTimestamp(response.data?.data?.daily.lastUpdated || new Date())
          );
          dispatch(homeAction.updateKpiEntry({ info: response.data.data.daily }));
        }
        if (payload?.competition?.length > 0) {
          handleCompetitionActivityUpdateOnSuccess(response.data.data.competition);
        }
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
        if (payload.daily) {
          dispatch(homeAction.updateKpiEntryFail({ kpiActionId: payload.daily.kpiActionId }));
        }
        if (payload?.competition?.length > 0) {
          handleCompetitionUpdateFail(payload.competition);
        }
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.response.data.message || error.message || "Something went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleActionEdit = (updatedActionData) => {
    if (updatedActionData.daily) {
      const actionPayload = handleCompetitionActivityChange(
        {
          ...updatedActionData,
          changeInCount: updatedActionData.count - updatedActionData.oldCount,
        },
        false
      );
      dispatch(homeAction.updateKpiActivityBeforeAPICall(updatedActionData));
      updateActivity({
        daily: {
          count: updatedActionData.count,
          points: updatedActionData.points,
          kpiActionId: updatedActionData.kpiActionId,
          requiredFields: updatedActionData?.requiredFields,
        },
        competition: actionPayload,
      });
    } else {
      const dailyActionIndex = KpiEntries.findIndex(
        (activity) => activity.kpiActionId === updatedActionData.kpiActionId
      );
      let dailyActionData;

      if (dailyActionIndex !== -1) {
        const newCount = generateCount(
          updatedActionData.oldCount,
          updatedActionData.count,
          KpiEntries?.[dailyActionIndex]?.DailyActions?.[0]?.count,
          updatedActionData.action
        );
        if (newCount !== KpiEntries?.[dailyActionIndex]?.DailyActions?.[0]?.count) {
          // dispatch(
          //   homeAction.updateKpiEntryDisabled({
          //     kpiActionId: updatedActionData.kpiActionId,
          //     disabled: true,
          //   })
          // );
          dailyActionData = {
            kpiActionId: updatedActionData.kpiActionId,
            count: newCount,
            points: calculateKpiPoints(
              KpiEntries?.[dailyActionIndex]?.count,
              KpiEntries?.[dailyActionIndex]?.points,
              newCount
            ),
            disabled: true,
            call: false,
          };
          dispatch(homeAction.updateKpiActivityBeforeAPICall(dailyActionData));
        }
      }
      const payload = handleCompetitionActivityChange(
        {
          ...updatedActionData,
          changeInCount: updatedActionData.count - updatedActionData.oldCount,
        },
        true
      );
      if (payload.length > 0) {
        updateActivity({
          daily: dailyActionData
            ? {
                count: dailyActionData.count,
                points: dailyActionData.points,
                kpiActionId: dailyActionData.kpiActionId,
                requiredFields: updatedActionData?.requiredFields,
              }
            : undefined,
          competition: payload,
        });
        // handleCompetitionActivityUpdate(payload, dailyActionData);
      }
    }
  };

  const handleSortByOrderOfKpi = (id, newSortByOrder) => {
    dispatch(homeAction.updateSortByOrderOfKpiSaga({ id, newSortByOrder }));
  };

  const refetchData = async () => {
    const dates = getFilterDates("Today", user.timezone);

    dispatch(homeAction.getDailyPointsInfoSaga());
    dispatch(homeAction.getKpiEntrySaga(dates));
  };

  const refetchAchievements = async () => {
    dispatch(homeAction.getUserAchievements());
  };

  useEffect(() => {
    if (!refreshAchievements) return;
    refetchAchievements();
  }, [refreshAchievements]);

  return (
    <MDBox sx={{ paddingBottom: "0rem", position: "relative" }}>
      <Loader loading={initialKpiEntryLoading || competitionLoading} />
      <MDBox mb={2} sx={{ position: "relative" }}>
        <Loader loading={KpiEntriesLoading} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CircularProgressChart
              kpiEntries={KpiEntries}
              targetPoints={targetPoints}
              lastUpdated={lastUpdated}
              activeStreak={activeStreak}
              pointGoals={pointGoals}
              workingDays={user.workingDays}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <WeeklyTotals
              selectedUser={selectedUser}
              ableToEditKpi
              refetchData={refetchData}
              selectedTab={selectedTab}
              pointGoals={pointGoals}
              view="user"
              handleCompetitionActivityUpdateOnSuccess={handleCompetitionActivityUpdateOnSuccess}
            />
          </Grid>
        </Grid>
      </MDBox>

      <UserKpi
        dailyInfo={dailyInfo}
        dailyInfoLoading={dailyInfoLoading}
        kpiEntries={KpiEntries}
        kpiEntriesLoading={KpiEntriesLoading}
        handleActionEdit={handleActionEdit}
        competitions={competitions}
        handleSortByOrderOfKpi={handleSortByOrderOfKpi}
        refetchKpiEntries={refetchKpiEntries}
      />

      {competitions.map((competition, index) => (
        <UserCompetition
          key={competition.id}
          competition={competition}
          kpiLocksDict={kpiLocksDict}
          removeCompetition={handleCompetitionRemove}
          handleActionEdit={handleActionEdit}
          setCompetitions={setCompetitions}
          competitionIndex={index}
        />
      ))}

      {isKpiTemplateModalOpen && (
        <KpiTemplateModal
          open={isKpiTemplateModalOpen}
          handleClose={handleKpiTemplateModalClose}
          showNotification={showNotification}
          user={user}
          refetchKpiEntries={refetchKpiEntries}
        />
      )}
    </MDBox>
  );
}

export default UserView;

UserView.propTypes = {
  selectedTab: PropTypes.number.isRequired,
  selectedUser: PropTypes.number.isRequired,
};
